/**
 * Email Limits admin page (iter107).
 *
 * Surfaces every email-send "point" in the system (quote send, cart
 * checkout, commissioning, warranty claim, etc.), how many emails each
 * has fired today, and the per-point + global daily caps. Lets the admin
 * adjust caps in real time so a runaway send loop or warranty-blast can't
 * burn through Gmail's daily quota and trigger account suspension.
 */
import { useEffect, useState, useCallback } from "react";
import { EnvelopeSimple, Warning, FloppyDisk, ArrowsClockwise, ListChecks } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import AdminPageHeader from "@/components/AdminPageHeader";
import { useT } from "@/lib/i18n";

export default function EmailLimitsPage({ user, onLogout, onLogoutAll }) {
  const t = useT();
  const [limits, setLimits] = useState(null);
  const [usage, setUsage] = useState(null);
  const [points, setPoints] = useState([]);
  const [skipped, setSkipped] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [r, s] = await Promise.all([
        api.get("/admin/email-limits"),
        api.get("/admin/email-limits/skipped?limit=20"),
      ]);
      setLimits(r.data.limits);
      setUsage(r.data.usage);
      setPoints(r.data.points);
      setSkipped(s.data.items || []);
      setDraft({
        enabled: r.data.limits.enabled,
        global_daily_cap: r.data.limits.global_daily_cap,
        per_recipient_daily_cap: r.data.limits.per_recipient_daily_cap,
        default_point_daily_cap: r.data.limits.default_point_daily_cap,
        point_caps: { ...r.data.limits.point_caps },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load email limits");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const saveDraft = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await api.put("/admin/email-limits", draft);
      toast.success(t("email_limits.saved", "Email limits saved"));
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("email_limits.save_failed", "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const resetCounters = async () => {
    if (!window.confirm(t("email_limits.reset_confirm",
      "Reset today's email counters to zero? This is the panic button after lifting an SMTP suspension."))) return;
    try {
      const r = await api.post("/admin/email-limits/reset", { confirm: true });
      toast.success(`${t("email_limits.reset_done", "Counters cleared")} (${r.data.deleted})`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    }
  };

  const [unblockingKey, setUnblockingKey] = useState("");
  const unblockRow = async (s) => {
    const scope = s.blocked_by_cap;
    const key = scope === "point" ? (s.point_id || "")
              : scope === "recipient" ? (s.to_email || "")
              : "";
    const rowKey = `${scope}:${key}`;
    if (unblockingKey) return;
    if (!window.confirm(t("email_limits.unblock_confirm",
      "Clear the {scope} counter for {target}? The next retry will be allowed through.",
      { scope, target: key || "(global)" }))) return;
    setUnblockingKey(rowKey);
    try {
      const r = await api.post("/admin/email-limits/unblock", { scope, key });
      if (r.data?.cleared) {
        toast.success(t("email_limits.unblock_done", "Unblocked — next retry will go through"));
      } else {
        toast.info(t("email_limits.unblock_already_clear",
          "Counter was already clear (likely already unblocked or rolled over to a new day)."));
      }
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("email_limits.unblock_failed", "Unblock failed"));
    } finally {
      setUnblockingKey("");
    }
  };

  const setCap = (pointId, value) => {
    setDraft((d) => {
      const next = { ...d, point_caps: { ...(d.point_caps || {}) } };
      if (value === "" || value === null) {
        delete next.point_caps[pointId];  // fall through to default
      } else {
        const n = Math.max(0, Math.min(2000, parseInt(value, 10) || 0));
        next.point_caps[pointId] = n;
      }
      return next;
    });
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  }
  const globalUsed = usage?.global || 0;
  const globalCap = draft?.global_daily_cap || 0;
  const globalPct = globalCap ? Math.min(100, Math.round((globalUsed / globalCap) * 100)) : 0;

  return (
    <div className="min-h-screen bg-slate-50 font-['Lato'] text-slate-900" data-testid="email-limits-page">
      <AdminPageHeader
        sticky
        maxWidth="max-w-6xl"
        testId="email-limits-back"
        icon={<EnvelopeSimple size={20} weight="fill" className="text-blue-700" />}
        title={t("email_limits.title", "Email Send Limits")}
        subtitle={t("email_limits.subtitle", "Prevent Gmail account suspension by capping daily sends.")}
        user={user}
        onLogout={onLogout}
        onLogoutAll={onLogoutAll}
      />

      <main className="max-w-6xl mx-auto p-6 space-y-6">

        {/* Global counter banner */}
        <section className={`rounded-md border-l-4 p-4 ${globalPct >= 100 ? "border-red-500 bg-red-50" : globalPct >= 80 ? "border-amber-500 bg-amber-50" : "border-emerald-500 bg-emerald-50"}`}
                 data-testid="email-limits-global-banner">
          <div className="flex items-baseline gap-3 flex-wrap">
            <h2 className="text-xs font-black uppercase tracking-wider text-slate-700">
              {t("email_limits.today_total", "Today's total")}:
            </h2>
            <span className="text-2xl font-mono font-black" data-testid="email-limits-global-used">
              {globalUsed} / {globalCap}
            </span>
            <span className="text-xs text-slate-500">
              {t("email_limits.date", "Date")}: {usage?.date}
            </span>
            <button onClick={resetCounters} data-testid="email-limits-reset-counters"
              className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-slate-600 hover:text-red-700 border border-slate-300 hover:border-red-300 px-2 py-1 rounded-sm">
              <ArrowsClockwise size={11} weight="bold" />
              {t("email_limits.reset_today", "Reset today's counters")}
            </button>
          </div>
          <div className="mt-2 h-2 rounded-sm bg-white/60 overflow-hidden">
            <div className={`h-full ${globalPct >= 100 ? "bg-red-500" : globalPct >= 80 ? "bg-amber-500" : "bg-emerald-500"}`}
                 style={{ width: `${globalPct}%` }} />
          </div>
        </section>

        {/* Global / default caps */}
        <section className="bg-white border rounded-md p-4 space-y-4">
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700">
            {t("email_limits.global_caps", "Global caps")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <CapInput
              label={t("email_limits.global_daily_cap", "Global daily cap")}
              hint={t("email_limits.global_daily_cap_hint", "Total emails sent across the system per UTC day. Gmail Workspace baseline is 2000/day; recommended cap: 250–500.")}
              testId="email-limits-global-cap"
              value={draft?.global_daily_cap}
              onChange={(v) => setDraft((d) => ({ ...d, global_daily_cap: v }))}
            />
            <CapInput
              label={t("email_limits.per_recipient_daily_cap", "Per-recipient daily cap")}
              hint={t("email_limits.per_recipient_daily_cap_hint", "Max emails to the same address per UTC day — protects against accidental loops blasting one inbox.")}
              testId="email-limits-per-recipient-cap"
              value={draft?.per_recipient_daily_cap}
              onChange={(v) => setDraft((d) => ({ ...d, per_recipient_daily_cap: v }))}
            />
            <CapInput
              label={t("email_limits.default_point_daily_cap", "Default per-point cap")}
              hint={t("email_limits.default_point_daily_cap_hint", "Cap applied to any send-point that doesn't have an explicit override below.")}
              testId="email-limits-default-cap"
              value={draft?.default_point_daily_cap}
              onChange={(v) => setDraft((d) => ({ ...d, default_point_daily_cap: v }))}
            />
          </div>
          <label className="flex items-center gap-2 text-xs font-bold text-slate-700">
            <input
              type="checkbox" data-testid="email-limits-enabled-toggle"
              checked={!!draft?.enabled}
              onChange={(e) => setDraft((d) => ({ ...d, enabled: e.target.checked }))}
            />
            {t("email_limits.enabled", "Throttling enabled (uncheck to disable all caps — Gmail-suspension risk)")}
          </label>
        </section>

        {/* Per-point caps */}
        <section className="bg-white border rounded-md p-4">
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-3">
            {t("email_limits.per_point_caps", "Per-point caps")}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="email-limits-points-table">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="px-2 py-2 text-left">{t("email_limits.col_point", "Send point")}</th>
                  <th className="px-2 py-2 text-right">{t("email_limits.col_used_today", "Used today")}</th>
                  <th className="px-2 py-2 text-right w-32">{t("email_limits.col_cap", "Cap (override)")}</th>
                </tr>
              </thead>
              <tbody>
                {points.map((p) => {
                  const draftCap = draft?.point_caps?.[p.point_id];
                  const effective = draftCap ?? draft?.default_point_daily_cap ?? 0;
                  const pct = effective ? Math.min(100, Math.round((p.used_today / effective) * 100)) : 0;
                  const tone = pct >= 100 ? "text-red-700"
                             : pct >= 80 ? "text-amber-700"
                             : "text-slate-700";
                  return (
                    <tr key={p.point_id} className="border-b border-slate-100 hover:bg-slate-50"
                        data-testid={`email-limits-row-${p.point_id}`}>
                      <td className="px-2 py-2">
                        <div className="font-mono text-xs font-bold">{p.point_id}</div>
                        <div className="text-[10px] text-slate-500">{p.label}</div>
                      </td>
                      <td className={`px-2 py-2 text-right font-mono ${tone}`}>
                        {p.used_today} / {effective}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <input
                          type="number" min="0" max="2000"
                          data-testid={`email-limits-cap-${p.point_id}`}
                          placeholder={String(draft?.default_point_daily_cap ?? "")}
                          value={draftCap ?? ""}
                          onChange={(e) => setCap(p.point_id, e.target.value)}
                          className="w-24 text-right text-sm border border-slate-300 rounded-sm px-2 py-1 focus:outline-none focus:border-blue-500"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] italic text-slate-500 mt-2">
            {t("email_limits.empty_field_hint", "Leave a cell empty to fall back to the default per-point cap above.")}
          </p>
        </section>

        {/* Save */}
        <div className="flex justify-end">
          <button
            onClick={saveDraft} disabled={saving}
            data-testid="email-limits-save"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-700 text-white text-sm font-bold uppercase tracking-wider rounded-sm hover:bg-blue-800 disabled:opacity-40">
            <FloppyDisk size={14} weight="fill" />
            {saving ? t("email_limits.saving", "Saving…") : t("email_limits.save", "Save limits")}
          </button>
        </div>

        {/* Recent skipped */}
        <section className="bg-white border rounded-md p-4">
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2 mb-3">
            <Warning size={14} weight="fill" className="text-amber-500" />
            {t("email_limits.recent_skipped", "Recent skipped sends")} ({skipped.length})
          </h2>
          {skipped.length === 0 ? (
            <p className="text-xs text-slate-500 italic">{t("email_limits.no_skipped", "No emails have been throttled yet — caps are not being hit.")}</p>
          ) : (
            <ul className="text-xs divide-y divide-slate-100" data-testid="email-limits-skipped-list">
              {skipped.map((s, i) => {
                const scope = s.blocked_by_cap;
                const rowKey = `${scope}:${scope === "point" ? (s.point_id || "")
                                         : scope === "recipient" ? (s.to_email || "")
                                         : ""}`;
                const busy = unblockingKey === rowKey;
                return (
                  <li key={i} className="py-2 flex flex-wrap items-center gap-3"
                      data-testid={`email-limits-skipped-row-${i}`}>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-red-50 text-red-700 border border-red-200">
                      {s.blocked_by_cap}
                    </span>
                    <span className="font-mono">{s.point_id}</span>
                    <span className="text-slate-500 truncate max-w-[200px]">→ {s.to_email}</span>
                    <span className="text-slate-400 truncate max-w-[300px]">{s.subject}</span>
                    <span className="ml-auto text-slate-400 font-mono">{s.count_at_block}/{s.cap}</span>
                    <span className="text-slate-400 text-[10px]">{new Date(s.created_at).toLocaleTimeString()}</span>
                    <button
                      onClick={() => unblockRow(s)}
                      disabled={busy || !!unblockingKey}
                      data-testid={`email-limits-unblock-${i}`}
                      title={t("email_limits.unblock_tooltip",
                        "Clear this exact counter so the next retry goes through")}
                      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40 disabled:cursor-not-allowed">
                      {busy
                        ? t("email_limits.unblocking", "Unblocking…")
                        : t("email_limits.unblock", "Unblock")}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Top recipients */}
        <section className="bg-white border rounded-md p-4">
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2 mb-3">
            <ListChecks size={14} weight="fill" className="text-violet-500" />
            {t("email_limits.top_recipients", "Top recipients today")}
          </h2>
          {(usage?.top_recipients || []).length === 0 ? (
            <p className="text-xs text-slate-500 italic">{t("email_limits.no_recipients", "No emails sent yet today.")}</p>
          ) : (
            <ul className="text-xs space-y-1">
              {usage.top_recipients.map((r, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="font-mono">{r.to}</span>
                  <span className="ml-auto text-slate-500 font-mono">{r.count}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

      </main>
    </div>
  );
}

function CapInput({ label, hint, value, onChange, testId }) {
  return (
    <label className="flex flex-col">
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</span>
      <input
        type="number" min="0" max="2000"
        value={value ?? ""}
        onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
        data-testid={testId}
        className="text-sm border border-slate-300 rounded-sm px-2 py-1.5 focus:outline-none focus:border-blue-500 font-mono"
      />
      <span className="text-[10px] italic text-slate-500 mt-1 leading-snug">{hint}</span>
    </label>
  );
}
